import { Fragment, type FC } from 'react';
import { FormattedNumber } from 'react-intl';

// MUI
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// screen
import { dishNoteSuffix, type Dish, type MealDay } from 'modules/map/components/nutrition-menu/types';
import SizedText from 'modules/map/components/pdf-print-form/styled-components/SizedText';
import PdfWeekday from 'modules/map/components/pdf-print-form/styled-components/PdfWeekday';
import {
  EXTRA_SMALL_FONT_SIZE,
  SMALL_FONT_SIZE,
} from 'modules/map/components/pdf-print-form/utils';

const dishPlaceholder: Dish = {
  cooked: '',
  name: '',
  raw: '',
  isFood: true,
};

interface SixDaysMenuProps {
  days: MealDay[];
  caloriesIncluded: boolean;
  shouldShowAfternoonMeals: boolean;
}

const MAX_DISHES_COUNT = 6;

const SixDaysMenu: FC<SixDaysMenuProps> = (props) => {
  const { days = [], caloriesIncluded, shouldShowAfternoonMeals } = props;

  const fontSize = caloriesIncluded ? SMALL_FONT_SIZE : EXTRA_SMALL_FONT_SIZE;

  return (
    <TableBody>
      {days.map((day, idx) => {
        const dishes = Array.from({ length: MAX_DISHES_COUNT }).map(
          (_, i) => day.dishes[i] ?? dishPlaceholder
        );
        const dishesCount = dishes.length;

        const shouldBreakNotes = day.note.includes('\n');
        const noteOfTheDay = shouldBreakNotes
          ? day.note.split('\n').map((note, index) => {
              return (
                <Typography key={index} sx={{ fontSize }}>
                  {note}
                </Typography>
              );
            })
          : day.note;

        return (
          <Fragment key={idx}>
            {dishes.map((dish, index) => {
              const isFirstRow = index === 0;
              let displayIndex = 1;
              dishes.slice(0, index).forEach((d) => {
                if (d.isFood) {
                  displayIndex++;
                }
              });

              const transparentBorderBottom = {
                borderBottom: `1px solid ${index === dishes.length - 1 ? 'auto' : 'transparent'}!important`,
              };

              const isIngredientOfImprovedDish = dish.isFood === false;

              return (
                <TableRow key={index}>
                  {isFirstRow && <PdfWeekday rowSpan={dishesCount} datetime={day.datetime} />}
                  <TableCell style={{ textAlign: 'left' }} sx={transparentBorderBottom}>
                    {isIngredientOfImprovedDish ? (
                      <SizedText sx={{ fontSize, px: '4px' }}>
                        - {dish.name}
                        {dishNoteSuffix(dish)}
                      </SizedText>
                    ) : (
                      <SizedText
                        sx={{
                          display: 'inline-block',
                          color: !dish.name.length ? 'transparent' : 'inherit',
                          px: '4px',
                          fontSize,
                        }}
                      >
                        {displayIndex}, {dish.name}
                        {dishNoteSuffix(dish)}
                      </SizedText>
                    )}
                  </TableCell>
                  <TableCell sx={transparentBorderBottom}>
                    <SizedText sx={{ fontSize }}>{dish.raw}</SizedText>
                  </TableCell>
                  <TableCell sx={transparentBorderBottom}>
                    <SizedText sx={{ fontSize }}>{dish.cooked}</SizedText>
                  </TableCell>
                  {isFirstRow && (
                    <>
                      {caloriesIncluded && (
                        <>
                          <TableCell rowSpan={dishesCount}>
                            <SizedText sx={{ fontSize }}>
                              {typeof day.total?.calorie === 'number' ? (
                                <FormattedNumber
                                  value={day.total.calorie}
                                  maximumFractionDigits={3}
                                />
                              ) : (
                                ''
                              )}
                            </SizedText>
                          </TableCell>
                          <TableCell rowSpan={dishesCount}>
                            <SizedText sx={{ fontSize }}>
                              {typeof day.total?.protein === 'number' ? (
                                <FormattedNumber
                                  value={day.total.protein}
                                  maximumFractionDigits={3}
                                />
                              ) : (
                                ''
                              )}
                            </SizedText>
                          </TableCell>
                          <TableCell rowSpan={dishesCount}>
                            <SizedText sx={{ fontSize }}>
                              {typeof day.total?.lipid === 'number' ? (
                                <FormattedNumber
                                  value={day.total.lipid}
                                  maximumFractionDigits={3}
                                />
                              ) : (
                                ''
                              )}
                            </SizedText>
                          </TableCell>
                          <TableCell rowSpan={dishesCount}>
                            <SizedText sx={{ fontSize }}>
                              {typeof day.total?.glucid === 'number' ? (
                                <FormattedNumber
                                  value={day.total.glucid}
                                  maximumFractionDigits={3}
                                />
                              ) : (
                                ''
                              )}
                            </SizedText>
                          </TableCell>
                        </>
                      )}
                      {shouldShowAfternoonMeals ? (
                        <TableCell rowSpan={dishesCount}>
                          <SizedText sx={{ fontSize }}>
                            {day.afternoons.dishes.map((meal, idx) => {
                              if (!meal) return <>&nbsp; </>;

                              const dishName = (meal[0] || '').replace(/\d\./, '');
                              const manufacturer = meal[1] || '';

                              return (
                                <Box sx={{ my: 1 }} key={idx}>
                                  <Typography
                                    sx={{
                                      userSelect: 'none',
                                      textAlign: 'center',
                                    }}
                                    variant="inherit"
                                  >
                                    {dishName}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      userSelect: 'none',
                                      textAlign: 'center',
                                    }}
                                    variant="inherit"
                                  >
                                    {manufacturer}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </SizedText>
                        </TableCell>
                      ) : null}

                      {caloriesIncluded && shouldShowAfternoonMeals ? (
                        <TableCell rowSpan={dishesCount}>
                          <SizedText sx={{ fontSize }}>{day.afternoons.calorie}</SizedText>
                        </TableCell>
                      ) : null}
                      <TableCell rowSpan={dishesCount}>
                        <SizedText sx={{ fontSize }}>{noteOfTheDay}</SizedText>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </Fragment>
        );
      })}
    </TableBody>
  );
};

export default SixDaysMenu;
